import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Unlock, Lock, Loader2, Undo2, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import { ProposalStatusBadge } from "@/components/proposals/ProposalStatusBadge";
import { InvoiceUploadModal } from "@/components/proposals/InvoiceUploadModal";
import { ProposalInvoiceBlock } from "@/components/proposals/ProposalInvoiceBlock";
import { ManagerReviewPanel } from "@/components/proposals/ManagerReviewPanel";
import { getStatusIndex, getStatusLabel, mapLegacyStatusToNewStatus, statusLocksEditing } from "@/lib/proposalStatus";
import {
  approveProposalCancellation,
  listProposalInvoices,
  lockProposalForEditing,
  unlockProposalForEditing,
  reactivateCanceledProposal,
  rejectProposalCancellation,
  revokeProposalCancellation,
  updateProposalStatus,
} from "@/lib/proposalsApi";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getUserColorToken, getUserInitials } from "@/lib/userIdentity";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CancellationDecisionType = "controllable" | "uncontrollable";

interface ProposalBuilderHeaderProps {
  proposalId: string | undefined;
  proposalTitle: string | null | undefined;
  proposalStatus: string | null | undefined;
  internalReference?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  ownerPhoto?: string | null;
  showOwnerInfo?: boolean;
  canUnlockProposal?: boolean;
  editUnlocked?: boolean | null;
  currentUserId?: string | null;
  currentUserRole?: string | null;
  cancellationReason?: string | null;
  onStatusChange: () => void;
  onInvoiceUploaded?: () => void;
}

export const ProposalBuilderHeader: React.FC<ProposalBuilderHeaderProps> = ({
  proposalId,
  proposalTitle,
  proposalStatus,
  internalReference,
  ownerId,
  ownerName,
  ownerPhoto,
  showOwnerInfo = false,
  canUnlockProposal = false,
  editUnlocked = false,
  currentUserId,
  currentUserRole,
  cancellationReason,
  onStatusChange,
  onInvoiceUploaded,
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [hasInvoice, setHasInvoice] = useState(false);
  const [checkingInvoice, setCheckingInvoice] = useState(true);
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);
  const [unlocking, setUnlocking] = useState(false);
  const [ceoOverrideOpen, setCeoOverrideOpen] = useState(false);
  const [ceoOverrideReason, setCeoOverrideReason] = useState("");
  const [savingCeoOverride, setSavingCeoOverride] = useState(false);
  const [revokingCancellation, setRevokingCancellation] = useState(false);
  const [reactivatingCancellation, setReactivatingCancellation] = useState(false);
  const [decisionType, setDecisionType] = useState<CancellationDecisionType | "">("");
  const [approvingCancellation, setApprovingCancellation] = useState(false);
  const [rejectingCancellation, setRejectingCancellation] = useState(false);
  const { toast } = useToast();

  const normalizedProposalStatus = mapLegacyStatusToNewStatus(proposalStatus);
  const approvedIndex = getStatusIndex("approved_to_be_paid");
  const currentStatusIndex = getStatusIndex(normalizedProposalStatus);
  const canShowInvoiceSection = currentStatusIndex >= approvedIndex;
  // Proposals can carry more than one invoice (e.g. split payments), so uploading stays
  // available for as long as the invoice section itself is shown, not just before the
  // first one lands.
  const canUploadInvoice = canShowInvoiceSection;
  const canUnlockForSalesFix = canUnlockProposal && normalizedProposalStatus === "approved_to_be_paid";
  const isCeo = currentUserRole === "ceo";
  // Distinct from "Unlock Editing" above, which hands the proposal back to Sales by moving it to
  // Needs Fix - Sales. This one is the CEO override: the status stays exactly where it is and only
  // the CEO can save while it is open.
  const statusFreezesEditing = statusLocksEditing(proposalStatus);
  const canUseCeoOverride = isCeo && statusFreezesEditing;
  const isCeoUnlocked = !!editUnlocked;
  const isSalesOwner = currentUserRole === "sales_staff" && !!currentUserId && !!ownerId && currentUserId === ownerId;
  const isManagerOrCeo = currentUserRole === "general_manager" || currentUserRole === "ceo";
  const canRevokeCancellation = isSalesOwner && normalizedProposalStatus === "in_review_to_be_canceled";
  const canReviewCancellation = isManagerOrCeo && normalizedProposalStatus === "in_review_to_be_canceled";
  const canReactivateCanceled = isManagerOrCeo && normalizedProposalStatus === "canceled";
  const displayOwnerName = (ownerName && ownerName.trim()) ? ownerName.trim() : "Unassigned";
  const ownerColor = getUserColorToken(ownerId || displayOwnerName);

  const decisionLabel = useMemo(() => {
    if (decisionType === "controllable") {
      return "with internal influence";
    }
    if (decisionType === "uncontrollable") {
      return "due to external factors";
    }
    return "";
  }, [decisionType]);

  useEffect(() => {
    if (proposalId && canShowInvoiceSection) {
      checkExistingInvoice();
    } else {
      setCheckingInvoice(false);
    }
  }, [proposalId, proposalStatus, canShowInvoiceSection]);

  const checkExistingInvoice = async () => {
    if (!proposalId) return;

    try {
      setCheckingInvoice(true);
      const invoices = await listProposalInvoices(proposalId);
      setHasInvoice(invoices.length > 0);
    } catch (error) {
      console.error("Error checking invoice:", error);
    } finally {
      setCheckingInvoice(false);
    }
  };

  const handleUploadSuccess = () => {
    // Only the FIRST invoice completes the approved_to_be_paid -> ready_to_book workflow
    // (booking package creation etc.), so only that one navigates away like before.
    // Additional (split-payment) invoices just refresh the list in place.
    const wasFirstInvoice = !hasInvoice;
    setHasInvoice(true);
    setInvoiceRefreshKey((key) => key + 1);
    if (wasFirstInvoice && onInvoiceUploaded) {
      onInvoiceUploaded();
      return;
    }
    onStatusChange();
  };

  const handleInvoiceLoaded = (exists: boolean) => {
    setHasInvoice(exists);
  };

  const handleUnlockProposal = async () => {
    if (!proposalId || !canUnlockForSalesFix || unlocking) return;
    try {
      setUnlocking(true);
      await updateProposalStatus(proposalId, { status: "needs_fix_sales" });
      toast({
        title: "Editing unlocked",
        description: "Proposal moved to Needs Fix – Sales.",
      });
      onStatusChange();
    } catch (error: any) {
      toast({
        title: "Unable to unlock editing",
        description: error?.message || "Failed to update proposal status.",
        variant: "destructive",
      });
    } finally {
      setUnlocking(false);
    }
  };

  const handleToggleCeoOverride = async (unlock: boolean) => {
    if (!proposalId || savingCeoOverride) return;
    try {
      setSavingCeoOverride(true);
      if (unlock) {
        await unlockProposalForEditing(proposalId, ceoOverrideReason.trim() || undefined);
        toast({
          title: "Proposal unlocked for editing",
          description: `Status stays ${getStatusLabel(proposalStatus)}. Only you can save changes.`,
        });
      } else {
        await lockProposalForEditing(proposalId);
        toast({ title: "Proposal locked again" });
      }
      setCeoOverrideOpen(false);
      setCeoOverrideReason("");
      onStatusChange();
    } catch (error: any) {
      toast({
        title: unlock ? "Unable to unlock proposal" : "Unable to lock proposal",
        description: error?.message || "Failed to change the edit lock.",
        variant: "destructive",
      });
    } finally {
      setSavingCeoOverride(false);
    }
  };

  const handleRevokeCancellation = async () => {
    if (!proposalId || revokingCancellation) return;
    try {
      setRevokingCancellation(true);
      await revokeProposalCancellation(proposalId);
      toast({
        title: "Request revoked",
        description: "The proposal returned to In Progress.",
      });
      onStatusChange();
    } catch (error: any) {
      toast({
        title: "Error revoking request",
        description: error?.message || "Could not revoke cancellation request.",
        variant: "destructive",
      });
    } finally {
      setRevokingCancellation(false);
    }
  };

  const handleApproveCancellation = async () => {
    if (!proposalId || approvingCancellation) return;
    if (!decisionType) {
      toast({
        title: "Decision type required",
        description: "Select cancellation type before approval.",
        variant: "destructive",
      });
      return;
    }

    try {
      setApprovingCancellation(true);
      await approveProposalCancellation(proposalId, { decision_type: decisionType });
      toast({
        title: "Cancelamento aprovado",
        description: `Proposal cancelada (${decisionLabel}).`,
      });
      setDecisionType("");
      onStatusChange();
    } catch (error: any) {
      toast({
        title: "Error approving cancellation",
        description: error?.message || "Could not approve cancellation.",
        variant: "destructive",
      });
    } finally {
      setApprovingCancellation(false);
    }
  };

  const handleRejectCancellation = async () => {
    if (!proposalId || rejectingCancellation) return;
    try {
      setRejectingCancellation(true);
      await rejectProposalCancellation(proposalId);
      toast({
        title: "Cancellation rejected",
        description: "The proposal returned to In Progress.",
      });
      setDecisionType("");
      onStatusChange();
    } catch (error: any) {
      toast({
        title: "Error rejecting cancellation",
        description: error?.message || "Could not reject cancellation.",
        variant: "destructive",
      });
    } finally {
      setRejectingCancellation(false);
    }
  };

  const handleReactivateCanceled = async () => {
    if (!proposalId || reactivatingCancellation) return;
    try {
      setReactivatingCancellation(true);
      await reactivateCanceledProposal(proposalId);
      toast({
        title: "Proposal reactivated",
        description: "The proposal returned to In Progress.",
      });
      onStatusChange();
    } catch (error: any) {
      toast({
        title: "Error reactivating proposal",
        description: error?.message || "Could not reactivate proposal.",
        variant: "destructive",
      });
    } finally {
      setReactivatingCancellation(false);
    }
  };

  if (!proposalId) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border bg-card p-4">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold">
              {proposalTitle || "Untitled Proposal"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {`Proposal #${(internalReference && internalReference.trim()) ? internalReference : proposalId.slice(0, 8)}`}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {canRevokeCancellation && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRevokeCancellation}
                  disabled={revokingCancellation}
                >
                  {revokingCancellation ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Revoking...
                    </>
                  ) : (
                    <>
                      <Undo2 className="mr-2 h-4 w-4" />
                      Revoke Cancellation Request
                    </>
                  )}
                </Button>
              )}

              {canReactivateCanceled && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReactivateCanceled}
                  disabled={reactivatingCancellation}
                >
                  {reactivatingCancellation ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Reactivating...
                    </>
                  ) : (
                    <>
                      <Undo2 className="mr-2 h-4 w-4" />
                      Reactivate Proposal
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          {showOwnerInfo && (
            <div className="ml-2 flex items-center gap-2 rounded-full border px-2.5 py-1.5" style={{ borderColor: ownerColor.border }}>
              <Avatar className="h-8 w-8 border" style={{ borderColor: ownerColor.border }}>
                <AvatarImage src={ownerPhoto || undefined} alt={displayOwnerName} />
                <AvatarFallback
                  className="text-[10px] font-semibold"
                  style={{ backgroundColor: ownerColor.bg, color: ownerColor.text }}
                >
                  {getUserInitials(displayOwnerName)}
                </AvatarFallback>
              </Avatar>
              <div className="leading-tight">
                <p className="text-[11px] text-muted-foreground">Responsible Owner</p>
                <p className="text-sm font-medium">{displayOwnerName}</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <ProposalStatusBadge status={proposalStatus} size="lg" />

          {canUnlockForSalesFix && (
            <Button
              type="button"
              variant="outline"
              onClick={handleUnlockProposal}
              disabled={unlocking}
            >
              {unlocking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock className="mr-2 h-4 w-4" />
                  Unlock Editing
                </>
              )}
            </Button>
          )}

          {canUseCeoOverride && (
            isCeoUnlocked ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => { void handleToggleCeoOverride(false); }}
                disabled={savingCeoOverride}
              >
                {savingCeoOverride ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Locking...
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Lock Proposal
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCeoOverrideOpen(true)}
                className="border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 hover:text-violet-900"
              >
                <ShieldAlert className="mr-2 h-4 w-4" />
                Unlock for Editing (CEO)
              </Button>
            )
          )}

          {canUploadInvoice && !checkingInvoice && (
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Upload className="mr-2 h-4 w-4" />
              {hasInvoice ? "Add Another Invoice (PDF)" : "Attach Invoice (PDF)"}
            </Button>
          )}
        </div>
      </div>

      {isCeoUnlocked && statusFreezesEditing && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-violet-300 bg-violet-50 px-4 py-3">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-violet-700" />
          <div className="text-sm text-violet-900">
            <p className="font-semibold">
              Unlocked for CEO editing - status stays {getStatusLabel(proposalStatus)}.
            </p>
            <p className="mt-0.5 text-violet-800">
              {isCeo
                ? "Only you can save changes while this is open. Lock it again when you are done."
                : "The CEO reopened this proposal. It stays read-only for everyone else."}
            </p>
          </div>
        </div>
      )}

      <Dialog open={ceoOverrideOpen} onOpenChange={setCeoOverrideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock this proposal for editing?</DialogTitle>
            <DialogDescription>
              The status stays {getStatusLabel(proposalStatus)} and the attached invoice is untouched.
              While unlocked, only you can save changes - Sales and Managers keep seeing it read-only.
              It stays open until you lock it again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="ceo-override-reason">Reason (optional)</Label>
            <Textarea
              id="ceo-override-reason"
              value={ceoOverrideReason}
              onChange={(event) => setCeoOverrideReason(event.target.value)}
              placeholder="Recorded in the proposal history."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCeoOverrideOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => { void handleToggleCeoOverride(true); }}
              disabled={savingCeoOverride}
            >
              {savingCeoOverride ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock className="mr-2 h-4 w-4" />
                  Unlock for Editing
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canReviewCancellation && (
        <div className="rounded-lg border border-orange-300 bg-orange-50/40 p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-orange-900">Cancellation review pending</h3>
            <p className="text-sm text-muted-foreground">
              {cancellationReason?.trim() || "No reason provided."}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cancellation-decision">Cancellation type (required)</Label>
            <Select
              value={decisionType}
              onValueChange={(value) => setDecisionType(value as CancellationDecisionType)}
            >
              <SelectTrigger id="cancellation-decision" className="w-full max-w-md">
                <SelectValue placeholder="Select decision type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="controllable">With internal influence</SelectItem>
                <SelectItem value="uncontrollable">Due to external factors</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleApproveCancellation}
              disabled={approvingCancellation || !decisionType}
              className="bg-red-600 hover:bg-red-700"
            >
              {approvingCancellation ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Approve Cancellation
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleRejectCancellation}
              disabled={rejectingCancellation}
            >
              {rejectingCancellation ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject Cancellation
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {canShowInvoiceSection && (
        <ProposalInvoiceBlock
          key={`${proposalId}-${invoiceRefreshKey}`}
          proposalId={proposalId}
          currentUserRole={currentUserRole}
          onInvoiceLoaded={handleInvoiceLoaded}
        />
      )}

      <ManagerReviewPanel
        proposalId={proposalId}
        proposalStatus={proposalStatus}
        onStatusChange={onStatusChange}
      />

      <InvoiceUploadModal
        key={proposalId}
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        proposalId={proposalId}
        onUploadSuccess={handleUploadSuccess}
        hasExistingInvoice={hasInvoice}
      />
    </div>
  );
};

export default ProposalBuilderHeader;
